import { commonLocations } from "@cartographerio/geometry";
import {
  nowTimestamp,
  randomSurveyId,
  randomUserId,
  randomWorkspaceId,
  SurveyStatus,
  SurveyStatusEnum,
  unsafeScreenName,
  unsafeSubdomain,
  UserRef,
  Project,
  userRef,
  WorkspaceV1,
  WorkspaceV2,
  Team,
  WorkspaceId,
} from "..";
import {
  Attachment,
  ContentType,
  attachmentFolder,
  AttachmentFolder,
  randomAttachmentId,
  unsafeContentType,
} from "../attachment";
import {
  DataLicenseEnum,
  ProjectId,
  randomProjectId,
  randomTeamId,
  SurveyId,
  SurveyModuleId,
} from "../core";
import { SurveyV1, SurveyV2, SurveyFV1, SurveyFV2 } from "../survey";

export function testWorkspaceV1(n: number): WorkspaceV1 {
  return {
    id: randomWorkspaceId(),
    name: `Workspace ${n}`,
    subdomain: unsafeSubdomain(`workspace${n}`),
    surveys: [],
    layers: [],
    maps: [],
    allowAnonymousSignup: true,
    sendSurveyApprovedEmails: true,
    sendSurveyCompleteEmails: true,
    mapTiles: "terrain",
    mapCenter: commonLocations.brighton.center,
    mapZoom: commonLocations.brighton.zoom,
  };
}

export function testWorkspaceV2(n: number): WorkspaceV2 {
  return {
    id: randomWorkspaceId(),
    name: `Workspace ${n}`,
    slug: unsafeSubdomain(`workspace${n}`),
    features: [],
  };
}

export function testProject(
  n: number,
  workspaceId: WorkspaceId = randomWorkspaceId()
): Project {
  return {
    id: randomProjectId(),
    name: `Project ${n}`,
    workspaceId,
    dataLicense: "Default",
    projectVisibility: "Workspace",
    surveyVisibility: "Workspace",
    mapVisibility: "Workspace",
    mapSettings: {
      defaultBounds: commonLocations.brighton.bounds,
      fallbackCenter: commonLocations.brighton.center,
      fallbackZoom: commonLocations.brighton.zoom,
    },
    emailSettings: {
      sendApprovedEmailsTo: [],
      sendApprovedEmailsToSurveyors: true,
      sendCompleteEmailsTo: [],
      sendCompleteEmailsToApprovers: true,
    },
    features: [],
    moduleIds: [],
    mapIds: [],
  };
}

export function testTeam(
  n: number,
  projectId: ProjectId = randomProjectId()
): Team {
  return {
    id: randomTeamId(),
    projectId,
    name: `Team ${n}`,
    mapSettings: null,
    emailSettings: {
      sendApprovedEmailsTo: [],
      sendApprovedEmailsToSurveyors: true,
      sendCompleteEmailsTo: [],
      sendCompleteEmailsToApprovers: true,
    },
  };
}

// Use this type in module-specific methods like testSurvey1() in the TestSurvey1 module:
export interface TestSurveyProps<A = unknown> {
  workspaceId: WorkspaceId;
  surveyor: UserRef;
  status?: SurveyStatus;
  data?: A;
}

export interface CompleteTestSurveyPropsV1<A = unknown>
  extends TestSurveyProps {
  type: SurveyModuleId;
  data: A;
}

export function testSurveyV1<A>(
  props: CompleteTestSurveyPropsV1<A>
): SurveyFV1<A> {
  const { type, workspaceId, surveyor, status, data } = props;

  return {
    id: randomSurveyId(),
    type,
    workspaceId,
    surveyor,
    status: status ?? SurveyStatusEnum.Draft,
    data,
    created: nowTimestamp(),
    updated: nowTimestamp(),
  };
}

export interface CompleteTestSurveyPropsV2<A = unknown>
  extends TestSurveyProps {
  type: SurveyModuleId;
  moduleId: SurveyModuleId;
  projectId: ProjectId;
  data: A;
}

export function testSurveyV2<A>(
  props: CompleteTestSurveyPropsV2<A>
): SurveyFV2<A> {
  const { moduleId, projectId, surveyor, status, data } = props;

  return {
    id: randomSurveyId(),
    moduleId,
    projectId,
    surveyor,
    status: status ?? SurveyStatusEnum.Draft,
    data,
    created: nowTimestamp(),
    updated: nowTimestamp(),
  };
}

export function testUserRef(n: number): UserRef {
  return userRef(unsafeScreenName(`UserRef ${n}`), randomUserId());
}

export interface TestAttachmentProps {
  surveyId: SurveyId;
  folder?: AttachmentFolder;
  author?: UserRef;
  contentType?: ContentType;
  filename?: string;
}

export function testAttachment(
  n: number,
  props: TestAttachmentProps
): Attachment {
  const { surveyId, folder, author, contentType, filename } = props;

  return {
    surveyId,
    folder: folder ?? attachmentFolder(),
    filename: filename ?? `attachment${n}.jpg`,
    contentType: contentType ?? unsafeContentType("image/jpeg"),
    author: author ?? userRef(),
    license: DataLicenseEnum.Default,
    created: nowTimestamp(),
    updated: nowTimestamp(),
    id: randomAttachmentId(),
  };
}

export interface TestAttachmentForSurveyProps {
  folder?: AttachmentFolder;
  author?: UserRef;
  contentType?: ContentType;
  filename?: string;
}

export function testAttachmentForSurveyV1(
  n: number,
  survey: SurveyV1,
  folder: AttachmentFolder
): Attachment {
  const { id: surveyId, surveyor } = survey;

  return {
    surveyId,
    folder,
    filename: `attachment${n}.jpg`,
    contentType: unsafeContentType("image/jpeg"),
    author: surveyor,
    license: DataLicenseEnum.Default,
    created: nowTimestamp(),
    updated: nowTimestamp(),
    id: randomAttachmentId(),
  };
}
export function testAttachmentForSurveyV2(
  n: number,
  survey: SurveyV2,
  folder: AttachmentFolder
): Attachment {
  const { id: surveyId, surveyor } = survey;

  return {
    surveyId,
    folder,
    filename: `attachment${n}.jpg`,
    contentType: unsafeContentType("image/jpeg"),
    author: surveyor,
    license: DataLicenseEnum.Default,
    created: nowTimestamp(),
    updated: nowTimestamp(),
    id: randomAttachmentId(),
  };
}
